import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, Plus, Calendar as CalendarIcon, Clock, Wrench, X, Save } from 'lucide-react';
import {
  format,
  addMonths,
  subMonths,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  isSameMonth,
  isToday,
  parseISO,
  isSameDay
} from 'date-fns';
import { es } from 'date-fns/locale';

const CalendarioCalibraciones = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newEvent, setNewEvent] = useState({
    date: format(new Date(), 'yyyy-MM-dd'),
    title: '',
    type: 'preventivo',
    time: '09:00',
    planta: 'PLANTA COPEC IQUIQUE'
  });

  const [scheduledEvents, setScheduledEvents] = useState([
    {
      id: 1,
      date: format(new Date(), 'yyyy-MM-dd'),
      title: 'Calibración Master Meter MM-01 (Mesa 200 B2)',
      type: 'preventivo',
      time: '09:00',
      planta: 'PLANTA COPEC IQUIQUE',
      status: 'pending'
    },
    {
      id: 2,
      date: format(addMonths(new Date(), 0), 'yyyy-MM-20'),
      title: 'Calibración T-200 Planta Norte',
      type: 'correctivo',
      time: '14:30',
      planta: 'PLANTA ANTOFAGASTA',
      status: 'pending'
    },
    {
      id: 3,
      date: format(new Date(), 'yyyy-MM-10'),
      title: 'Verificación Volumétrica Brazo 1',
      type: 'rutina',
      time: '10:30',
      planta: 'PLANTA COPEC IQUIQUE',
      status: 'completed'
    }
  ]);

  const nextMonth = () => setCurrentDate(addMonths(currentDate, 1));
  const prevMonth = () => setCurrentDate(subMonths(currentDate, 1));

  const daysInMonth = eachDayOfInterval({
    start: startOfMonth(currentDate),
    end: endOfMonth(currentDate)
  });

  const firstDayOfMonth = startOfMonth(currentDate).getDay();
  const emptyDays = Array(firstDayOfMonth === 0 ? 6 : firstDayOfMonth - 1).fill(null);

  const getTypeColor = (type) => {
    switch (type) {
      case 'preventivo':
        return 'bg-blue-100 text-[#005f88] border-[#b8d6e7]';
      case 'correctivo':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'rutina':
        return 'bg-emerald-100 text-emerald-800 border-emerald-200';
      default:
        return 'bg-slate-100 text-slate-700 border-slate-200';
    }
  };

  const handleAddEvent = (e) => {
    e.preventDefault();
    if (!newEvent.title) return;
    setScheduledEvents((prev) => [
      ...prev,
      { ...newEvent, id: Date.now(), status: 'pending' }
    ]);
    setNewEvent({
      date: format(new Date(), 'yyyy-MM-dd'),
      title: '',
      type: 'preventivo',
      time: '09:00',
      planta: 'PLANTA COPEC IQUIQUE'
    });
    setIsModalOpen(false);
  };

  return (
    <div className="flex flex-col h-full w-full gap-2.5">
      {/* Top Bar */}
      <div className="bg-white p-2.5 rounded shadow-sm border border-slate-200 flex flex-wrap justify-between items-center gap-3">
        <div className="flex items-center gap-2">
          <div className="bg-[#005f88] text-white p-1.5 rounded">
            <CalendarIcon className="w-4 h-4" />
          </div>
          <div>
            <h2 className="text-sm font-bold text-[#005f88] uppercase tracking-wide">
              Cronograma y Programación de Calibraciones
            </h2>
            <p className="text-[11px] text-slate-500">
              Planificación mensual de mantenimiento preventivo, correctivo y verificaciones
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setCurrentDate(new Date())}
            className="px-3 py-1.5 text-xs font-bold text-[#005f88] bg-[#eaf4f8] hover:bg-[#d5e8f2] border border-[#b8d6e7] rounded transition-colors"
          >
            Hoy
          </button>
          <div className="flex items-center bg-white rounded border border-[#b8d6e7]">
            <button
              type="button"
              onClick={prevMonth}
              className="p-1.5 text-slate-600 hover:text-[#005f88] hover:bg-[#eaf4f8] transition-colors"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="px-3 text-xs font-bold text-[#005f88] uppercase min-w-[130px] text-center">
              {format(currentDate, 'MMMM yyyy', { locale: es })}
            </span>
            <button
              type="button"
              onClick={nextMonth}
              className="p-1.5 text-slate-600 hover:text-[#005f88] hover:bg-[#eaf4f8] transition-colors"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
          <button
            type="button"
            onClick={() => setIsModalOpen(true)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#006699] hover:bg-[#0854A0] text-white rounded text-xs font-bold shadow-sm transition-colors"
          >
            <Plus className="w-4 h-4" />
            Agendar Calibración
          </button>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="flex-1 grid grid-cols-1 lg:grid-cols-12 gap-2.5 min-h-0">
        {/* Left Sidebar: Upcoming Tasks */}
        <div className="lg:col-span-3 bg-white rounded shadow-sm border border-[#005f88] flex flex-col overflow-hidden">
          <div className="bg-[#005f88] text-white px-3 py-2 text-xs font-bold uppercase tracking-wider border-b border-[#004a6b]">
            Próximas Tareas Programadas
          </div>
          <div className="p-3 space-y-2.5 overflow-y-auto flex-1">
            {scheduledEvents
              .sort((a, b) => new Date(a.date) - new Date(b.date))
              .map((ev) => (
                <div
                  key={ev.id}
                  className="p-2.5 rounded border border-[#cde0eb] bg-[#eaf4f8]/60 hover:bg-[#d5e8f2]/70 transition-colors"
                >
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-[11px] font-bold text-[#0A6ED1]">
                      {format(parseISO(ev.date), 'dd MMM yyyy', { locale: es })}
                    </span>
                    <span className="text-[10px] bg-white border border-[#b8d6e7] text-slate-700 px-1.5 py-0.5 rounded font-mono flex items-center gap-1">
                      <Clock className="w-3 h-3 text-[#005f88]" /> {ev.time}
                    </span>
                  </div>
                  <div className="text-xs font-bold text-slate-800 leading-snug">{ev.title}</div>
                  <div className="mt-1.5 flex items-center justify-between">
                    <span className="text-[10px] text-slate-500 flex items-center gap-1">
                      <Wrench className="w-3 h-3 text-[#005f88]" /> {ev.planta || 'Planta'}
                    </span>
                    <span
                      className={`text-[10px] px-1.5 py-0.5 rounded border font-bold uppercase ${getTypeColor(
                        ev.type
                      )}`}
                    >
                      {ev.type}
                    </span>
                  </div>
                </div>
              ))}
          </div>
        </div>

        {/* Right: Calendar Grid */}
        <div className="lg:col-span-9 bg-white rounded shadow-sm border border-[#005f88] flex flex-col overflow-hidden">
          <div className="grid grid-cols-7 bg-[#005f88] text-white border-b border-[#004a6b]">
            {['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'].map(
              (day) => (
                <div
                  key={day}
                  className="py-2 text-center text-xs font-bold uppercase tracking-wider border-r border-[#004a6b] last:border-r-0"
                >
                  <span className="hidden sm:inline">{day}</span>
                  <span className="sm:hidden">{day.slice(0, 3)}</span>
                </div>
              )
            )}
          </div>

          <div className="flex-1 grid grid-cols-7 auto-rows-fr overflow-y-auto">
            {emptyDays.map((_, i) => (
              <div
                key={`empty-${i}`}
                className="border-r border-b border-[#cde0eb] bg-slate-50/70 min-h-[100px]"
              />
            ))}

            {daysInMonth.map((day) => {
              const dayEvents = scheduledEvents.filter((ev) =>
                isSameDay(parseISO(ev.date), day)
              );
              const isCurrentMonth = isSameMonth(day, currentDate);
              const isTodayDate = isToday(day);

              return (
                <div
                  key={day.toString()}
                  className={`border-r border-b border-[#cde0eb] min-h-[100px] p-1.5 flex flex-col transition-colors hover:bg-[#eaf4f8]/60 ${
                    !isCurrentMonth ? 'bg-slate-50/50' : 'bg-white'
                  }`}
                >
                  <div className="flex justify-between items-center mb-1">
                    <span
                      className={`w-6 h-6 flex items-center justify-center rounded text-xs font-bold ${
                        isTodayDate
                          ? 'bg-[#005f88] text-amber-300 shadow-xs'
                          : 'text-slate-700'
                      }`}
                    >
                      {format(day, 'd')}
                    </span>
                    {dayEvents.length > 0 && (
                      <span className="text-[10px] font-bold text-[#0A6ED1] bg-blue-50 px-1.5 rounded">
                        {dayEvents.length}
                      </span>
                    )}
                  </div>

                  <div className="flex-1 space-y-1 overflow-y-auto">
                    {dayEvents.map((ev) => (
                      <div
                        key={ev.id}
                        className={`text-[11px] p-1 rounded border leading-tight font-semibold ${getTypeColor(
                          ev.type
                        )}`}
                      >
                        <div className="font-mono text-[9px] opacity-80">{ev.time}</div>
                        <div className="truncate">{ev.title}</div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Modal Agendar Calibración */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded shadow-2xl border border-[#005f88] w-full max-w-md overflow-hidden">
            <div className="bg-[#005f88] text-white px-4 py-2.5 flex items-center justify-between border-b border-[#004a6b]">
              <h3 className="font-bold text-xs uppercase tracking-wider">
                Agendar Nueva Calibración
              </h3>
              <button
                type="button"
                onClick={() => setIsModalOpen(false)}
                className="text-cyan-100 hover:text-white p-1"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <form onSubmit={handleAddEvent} className="p-4 space-y-3 text-xs">
              <div>
                <label className="block font-bold text-slate-600 uppercase mb-1">
                  Descripción / Equipo *
                </label>
                <input
                  type="text"
                  required
                  value={newEvent.title}
                  onChange={(e) => setNewEvent({ ...newEvent, title: e.target.value })}
                  placeholder="Ej: Calibración MM-01 Mesa 200 Brazo 2"
                  className="w-full px-2.5 py-1.5 border border-[#b8d6e7] rounded focus:border-[#0A6ED1] outline-none"
                />
              </div>
              <div>
                <label className="block font-bold text-slate-600 uppercase mb-1">
                  Planta / Terminal
                </label>
                <input
                  type="text"
                  value={newEvent.planta}
                  onChange={(e) => setNewEvent({ ...newEvent, planta: e.target.value })}
                  className="w-full px-2.5 py-1.5 border border-[#b8d6e7] rounded focus:border-[#0A6ED1] outline-none"
                />
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <label className="block font-bold text-slate-600 uppercase mb-1">Fecha</label>
                  <input
                    type="date"
                    value={newEvent.date}
                    onChange={(e) => setNewEvent({ ...newEvent, date: e.target.value })}
                    className="w-full px-2 py-1.5 border border-[#b8d6e7] rounded focus:border-[#0A6ED1] outline-none"
                  />
                </div>
                <div>
                  <label className="block font-bold text-slate-600 uppercase mb-1">Hora</label>
                  <input
                    type="time"
                    value={newEvent.time}
                    onChange={(e) => setNewEvent({ ...newEvent, time: e.target.value })}
                    className="w-full px-2 py-1.5 border border-[#b8d6e7] rounded focus:border-[#0A6ED1] outline-none"
                  />
                </div>
                <div>
                  <label className="block font-bold text-slate-600 uppercase mb-1">Tipo</label>
                  <select
                    value={newEvent.type}
                    onChange={(e) => setNewEvent({ ...newEvent, type: e.target.value })}
                    className="w-full px-2 py-1.5 border border-[#b8d6e7] rounded focus:border-[#0A6ED1] outline-none bg-white"
                  >
                    <option value="preventivo">Preventivo</option>
                    <option value="correctivo">Correctivo</option>
                    <option value="rutina">Rutina</option>
                  </select>
                </div>
              </div>
              <div className="pt-3 border-t border-slate-200 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-3 py-1.5 rounded border border-slate-300 text-slate-700 hover:bg-slate-100 font-semibold"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-4 py-1.5 rounded bg-[#005f88] hover:bg-[#004a6b] text-white font-bold flex items-center gap-1.5"
                >
                  <Save className="w-3.5 h-3.5" />
                  Guardar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CalendarioCalibraciones;
